<script>
import ModelEdit from '@/components/models/edit-v2';
import FormsModel from "@/components/forms/model";
import appConfig from "@/app.config.json";
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import { models } from './data-models';
import Swal from "sweetalert2";

export default {
  page: {
    title: "Models",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    ModelEdit
  },
  props: {
    cardTitle: { default() { return this.lodash.startCase(this.$t('models.singular')) } },
    resourceRoute: { default: 'models' },
    modelVariable: { default: 'model' },
    form: { default() { return FormsModel; } }
  },
  data() {
    return {
      title: this.lodash.upperFirst(this.$t('models.plural')),
      loading: false,
      updating: false,
      deleting: false,
      model: this.modelVariable === 'model' ? (models[this.$route.params.key - 1] || {}) : {},
      errors: {},
      isEditing: false,
    }
  },
  computed: {
    items() {
      return [
        {
          text: this.lodash.upperFirst(this.$t('models.plural')),
          href: "/models",
        },
        {
          text: this.model && this.model.id ? this.model.name : this.$route.params.key,
          href: `/models/${this.$route.params.key}`,
          active: true,
        },
        {
          text: this.$t('Edit'),
          href: `/models/${this.$route.params.key}/edit`,
          active: true,
        },
      ]
    }
  },
  methods: {
    getModelKey() {
      return this.model[this.modelKey || 'id'];
    },
    getModelName() {
      return this.model['name'] || this.getModelKey();
    },
    handleModelChanged(event) {
      this.isEditing = event;
    },
    handleBack() {
      this.$router.back()
    },
    failedAlert(error) {
      if (error.response && error.response.status === 422) {
        this.errors = (error.response.data || {}).errors || {};
        const key = Object.keys(this.errors).find((k) => this.errors[k].length)
        const errorMessage = key && this.errors[key][0]
        return Swal.fire({
          title: this.$t('Failed') + '!',
          text: errorMessage || (error.response && (error.response.data || {}).message) || error.message,
          type: 'danger',
          confirmButtonClass: 'btn btn-danger btn-fill',
          buttonsStyling: false
        })
      }
      return Swal.fire({
        title: this.$t('Failed') + '!',
        text: (error.response && (error.response.data || {}).message) || error.message,
        type: 'danger',
        confirmButtonClass: 'btn btn-danger btn-fill',
        buttonsStyling: false
      })
    },
    updateSuccessAlert(something) {
      return Swal.fire({
        title: this.$t('Success') + '!',
        text: this.$t(`You've updated {something}`, { something }),
        type: 'success',
        showConfirmButton: false,
        timer: 1500
      })
    },
    update(value) {
      return Promise.resolve({ data: value })
    },
    handleUpdate(value) {
      this.updating = true;
      this.update(value)
        .then(() => this.updateSuccessAlert(this.getModelName()))
        .then(() => {
          this.isEditing = false;
          return this.$router.back()
        })
        .catch((error) => this.failedAlert(error))
        .finally(() => { this.updating = false; });
    },
    deleteConfirmAlert() {
      return Swal.fire({
        title: this.$t('Are you sure?'),
        text: this.$t(`You won't be able to revert this!`),
        type: 'warning',
        showCancelButton: true,
        confirmButtonClass: 'btn btn-success btn-fill mx-1',
        cancelButtonClass: 'btn btn-danger btn-fill mx-1',
        confirmButtonText: this.$t('Yes, delete it!'),
        cancelButtonText: this.$t('Cancel'),
        buttonsStyling: false
      })
    },
    deleteSuccessAlert(something) {
      return Swal.fire({
        title: this.$t('Deleted') + '!',
        text: this.$t(`You deleted {something}`, { something }),
        type: 'success',
        confirmButtonClass: 'btn btn-success btn-fill',
        buttonsStyling: false
      })
    },
    delete() {
      return Promise.resolve({ data: {} })
    },
    handleDelete() {
      this.deleteConfirmAlert().then(result => {
        if (result.value) {
          this.deleting = true;
          this.delete()
            .then(() => this.deleteSuccessAlert(this.getModelName()))
            .then(() => this.$router.back())
            .catch((error) => this.failedAlert(error))
            .finally(() => { this.deleting = false; });
        }
      });
    },
    preventNav() {
      if (this.isEditing) {
        return this.$t("All unsaved data will be lost. Are you sure?");
      }
      return null
    }
  },
  beforeMount() {
    window.onbeforeunload = this.preventNav
  },
  beforeDestroy() {
    window.onbeforeunload = null
  },
  beforeRouteLeave(to, from, next) {
    if (this.isEditing) {
      if (!window.confirm(this.lodash.upperFirst(this.$t("All unsaved data will be lost. Are you sure?")))) {
        return;
      }
    }
    next();
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <model-edit class="col-12" :loading="loading" :updating="updating" :deleting="deleting" v-bind="$props"
        v-model="model" :errors.sync="errors" :key="getModelKey()" @back-click="handleBack" @update-click="handleUpdate"
        @delete-click="handleDelete" @model-changed="handleModelChanged">
      </model-edit>
    </div>
  </Layout>
</template>

<style scoped lang="scss">
::v-deep {
  .page-content {
    background: #EEF3F7;
    font-family: Lato;
    color: #161B4D;
  }

  .page-title {
    font-size: 14px !important;
    color: #6C7589;
    font-family: Lato;
  }

  .page-title-box {
    padding-bottom: 8px;
  }
}
</style>